import { CampaignApplication } from "types/campaign/applications"
import React, { useCallback, useState } from "react"
import InfluencerCardHeader from "Components/influencer/InfluencerCardHeader"
import { Spinner } from "reactstrap"
import { AppDispatch, RootState, store } from "store"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { showInfluencerPostModal, showSocialPostModal, toggleApplicationView } from "slices/ui/reducer"
import InfluencerStats from "Components/influencer/InfluencerStats"
import { bookmarkApplciation } from "slices/campaigns/applications/thunk"
import { ACCOUNT_NOT_FOUND_MSG } from "constants/global"
import Swal from "sweetalert2"
import { campaignIconSVG } from "pages/Campaigns/Campaign"
import { formatAmount, formatNumber, getErrorMessage } from "utils/helpers"
import { updateApplicationStatus } from "slices/campaigns/applications/reducer"
import { PRIVILEGE } from "constants/privilege"
import { usePrivileges } from "hooks/useAuth"
import { processApplication as processApplicationAPI } from "services/api/campaign/application"
import { updateRemainingBudget } from "slices/campaigns/reducer"
import userProfilePicture from "assets/images/users/user-dummy-img.jpg"
import "./style.scss"
import TrashIcon from "assets/icons/TrashIcon"
import { useGetCampaignDataById } from "slices/campaigns/selector"

export interface ActionConfirmation {
    action: 'approve' | 'reject',
    onConfirmation: () => void
}

const ApplicationCard: React.FC<{ application: CampaignApplication, campaignId: string, viewBestCreative?: boolean }> = ({ application, campaignId, viewBestCreative = false }) => {

    const dispatch: AppDispatch = useDispatch();
    const [bookmarking, setBookmarking] = useState(false);
    const shortlistExit = useSelector((state: RootState) => state.ui.shortlistExitAnimation);
    const applicationStatus = application.status;
    const isShortlisted = application.bookmarked === 'y';
    const privileges = usePrivileges();

    const campaign = useGetCampaignDataById(campaignId);

    const negotiableBudget = campaign?.fixed_offer === 'n';
    const quotedOffer = negotiableBudget && application.quoted_offer ? Number(application.quoted_offer) : NaN;
    const quotedOfferLabel = isNaN(quotedOffer)
        ? null
        : isShortlisted
            ? `Shortlisted at ${formatAmount(quotedOffer)}`
            : `Requested a payout of ${formatAmount(quotedOffer)}`;

    // Exit animation only when leaving Applications (not on Shortlisted tab where bookmarked === 'y').
    const isExitingShortlist =
        !isShortlisted &&
        shortlistExit?.applicationId === application.uuid &&
        shortlistExit?.campaignId === campaignId;

    const bookmark = async (value: 'y' | 'n', force: boolean = false) => {
        if (bookmarking) return;
        const animateShortlist = value === 'y' && !isShortlisted;
        try {
            setBookmarking(true);
            const result = await dispatch(
                bookmarkApplciation({
                    campaignId,
                    applicationId: application.uuid,
                    bookmark: value,
                    animateShortlist,
                    force,
                })
            );
            if (bookmarkApplciation.rejected.match(result)) {
                const errorCode = result.payload?.errorCode ?? 'unknown_error';
                const message = result.payload?.message ?? 'Failed to shortlist this application';
                if (errorCode && ['existing_ongoing_collabs', 'application_restricted'].includes(errorCode)) {
                    const r = await Swal.fire({
                        title: message,
                        iconHtml: campaignIconSVG,
                        showCancelButton: true,
                        confirmButtonText: "Shortlist Anyway",
                        cancelButtonText: "Cancel",
                        reverseButtons: true
                    });
                    if (r.isConfirmed) {
                        return bookmark(value, true);
                    }
                } else {
                    toast.error(message);
                }
            }
            // if (animateShortlist && bookmarkApplciation.fulfilled.match(result)) {
            //     toast.success('Moved to Shortlisted', { autoClose: 2200 });
            // }
        } catch (error: any) {
            console.log('error');
        } finally {
            setBookmarking(false);
        }
    };

    const processApplication = useCallback(async (applicationId: string, action: 'accept' | 'reject', force: boolean = false) => {
        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to process application!");
            return;
        }

        const r = await Swal.fire({
            title: `Are you sure you want to ${action === 'accept' ? 'accept' : 'reject'} this application?`,
            iconHtml: campaignIconSVG,
            showCancelButton: true,
            confirmButtonText: "Yes, Proceed",
            cancelButtonText: "No, Cancel",
            reverseButtons: true
        });

        if (!r.isConfirmed) return;

        try {
            dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: 'processing' }));
            const agencyId = store.getState().auth.selectedAgencyId;
            if (!agencyId) throw new Error("Agency not selected!");
            const result = await processApplicationAPI(agencyId, campaignId, applicationId, { action, force });
            if (result.status && result.application?.status) {
                dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: result.application.status }));
            }
            if (result.campaign?.remaining_budget) {
                dispatch(updateRemainingBudget({ campaignId, budget: result.campaign.remaining_budget }));
            }
        } catch (error: any) {
            if (error?.response?.data?.error_code === "existing_ongoing_collabs") {
                const r = await Swal.fire({
                    title: error?.response?.data?.message ?? "This creator already has ongoing collaborations in other campaigns",
                    iconHtml: campaignIconSVG,
                    showCancelButton: true,
                    confirmButtonText: "Approve Anyway",
                    cancelButtonText: "Cancel",
                    reverseButtons: true
                });

                if (r.isConfirmed) {
                    return processApplication(applicationId, action, true);
                }
                dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: 'pending' }));
                return;
            }
            toast.error(getErrorMessage(error, "Failed to process application!"));
            dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: 'pending' }));
        }
    }, [campaignId, dispatch]);

    const influencerName =
        application.influencer?.name ??
        (application?.social_account?.name ?? `@${application.social_account?.handle}`);

    const rejectButton = applicationStatus === 'pending' && (
        <span
            className={`application-action navigate-icon text-danger application-card__reject-btn`}
            role="button"
            aria-label="Reject application"
            onClick={(e) => {
                e.stopPropagation();
                processApplication(application.uuid, 'reject');
            }}
        >
            <TrashIcon size={20} />
        </span>
    );

    return (
        <React.Fragment>
            <div
                className={`application-card position-relative cursor-pointer ${isExitingShortlist ? 'application-card--shortlist-exit' : ''}${viewBestCreative ? ' application-card--best-creative' : ''}`}
                onClick={e => {
                    dispatch(toggleApplicationView({ applicationId: application.uuid, campaignId, socialAccountId: application.social_account?.uuid }))
                }}>
                {rejectButton}
                {viewBestCreative ? (
                    <>
                        {application.social_post?.thumbnail_url ? (
                            <div className="application-card__creative cursor-pointer" onClick={(e: any) => {
                                e.stopPropagation();
                                if (!application.social_post?.uuid) return;
                                dispatch(showSocialPostModal({
                                    postId: application.social_post?.uuid,
                                    influencer: application.influencer,
                                    socialAccount: application.social_account,
                                }))
                            }}>
                                <img
                                    className="application-card__creative-img"
                                    src={application.social_post.thumbnail_url}
                                    alt="Best creative"
                                    referrerPolicy="no-referrer"
                                />
                                <span className="application-card__best-creative-badge">Best Creative</span>
                                <span className="application-card__creative-play" aria-hidden>
                                    <i className="ri-play-circle-fill" />
                                </span>
                                <div className="application-view-media__card-overlay justify-content-end">
                                    <span className="application-view-media__card-stat">
                                        <i className="mdi mdi-eye" aria-hidden />
                                        {formatNumber(application.social_post.views_count)}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="application-card__creative application-card__creative--placeholder">
                                <div className="application-card__creative-placeholder-inner">
                                    <i className="ri-image-2-line application-card__creative-placeholder-icon" aria-hidden />
                                    <p className="application-card__creative-placeholder-text">No creative shared yet</p>
                                </div>
                            </div>
                        )}
                        {application.social_account ? (
                            <>
                                <div className="application-card__profile">
                                    <div className="application-card__profile-avatar-wrap">
                                        <img
                                            className="application-card__profile-avatar"
                                            src={application.social_account.profile_picture_url?.split('=')[0] ?? userProfilePicture}
                                            alt={influencerName}
                                            referrerPolicy="no-referrer"
                                        />
                                        {application.social_account.platform === 'ig' && (
                                            <div className="ig-logo-space">
                                                <div className="ig-logo" />
                                            </div>
                                        )}
                                        {application.social_account.platform === 'yt' && (
                                            <div className="ig-logo-space">
                                                <i className="ri-youtube-fill fs-16px text-danger" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="application-card__profile-info">
                                        <p className="application-card__profile-name">{influencerName}</p>
                                    </div>
                                </div>
                                <div className="px-2">
                                    <div className="application-card__stats social-account-stats social-account-stats--cols-3">
                                        <div className="social-account-stats__cell">
                                            <p className="social-account-stats__value">
                                                {application.social_account.engagement_score ?? 'NA'}
                                            </p>
                                            <p className="social-account-stats__label">Engg Score</p>
                                        </div>
                                        <div className="social-account-stats__cell">
                                            <p className="social-account-stats__value">
                                                {application.average_views
                                                    ? formatNumber(parseInt(application.average_views))
                                                    : 'NA'}
                                            </p>
                                            <p className="social-account-stats__label">Avg Views</p>
                                        </div>
                                        <div className="social-account-stats__cell">
                                            <p className="social-account-stats__value">
                                                {formatNumber(application.social_account.followers_count)}
                                            </p>
                                            <p className="social-account-stats__label">Followers</p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="account-not-available-card">
                                <span className="w-75">{ACCOUNT_NOT_FOUND_MSG}</span>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <InfluencerCardHeader
                            name={application.influencer?.name ?? (application?.social_account?.name ?? `@${application.social_account?.handle}`)}
                            socialAccount={application.social_account}
                            language={application?.language?.name}
                        />
                        {
                            application.social_account ? (
                                <InfluencerStats
                                    average_views={application.average_views}
                                    engagement_color={application.social_account?.engagement_rate_details.color}
                                    engagement_score={application.social_account?.engagement_score}
                                    authenticity_color={application.authenticity_score_details.color}
                                    authenticity_score={application.authenticity_score}
                                    requirement_match_color={application.requirement_match_details.color}
                                    requirement_match={application.requirement_match}
                                    followers_count={application.social_account.followers_count}
                                />
                            ) : (
                                <div className="account-not-available-card">
                                    <span className="w-75">{ACCOUNT_NOT_FOUND_MSG}</span>
                                </div>
                            )
                        }
                    </>
                )}

                {quotedOfferLabel && (
                    <div
                        className={`mt-2 application-card__quote${isShortlisted ? ' application-card__quote--accepted' : ''}`}
                    >
                        {quotedOfferLabel}
                    </div>
                )}

                {isExitingShortlist && (
                    <div className="application-card-shortlist-flyout" aria-hidden>
                        <span className="application-card-shortlist-flyout__icon">→</span>
                        <span>Shortlisted</span>
                    </div>
                )}
                {
                    applicationStatus === 'pending' && (
                        <div className="application-card-shortlist-bar" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className={`theme-btn application-card-shortlist-btn w-100 ${isShortlisted ? 'application-card-shortlist-btn--shortlisted' : 'btn-primary'
                                    }`}
                                disabled={bookmarking}
                                onClick={() => bookmark(isShortlisted ? 'n' : 'y')}
                            >
                                {bookmarking ? (
                                    <Spinner size="sm" color={isShortlisted ? 'primary' : 'light'} />
                                ) : isShortlisted ? (
                                    <>
                                        <span className="application-card-shortlist-btn__label application-card-shortlist-btn__label--default">
                                            SHORTLISTED
                                        </span>
                                        <span className="application-card-shortlist-btn__label application-card-shortlist-btn__label--hover">
                                            REMOVE FROM SHORTLIST
                                        </span>
                                    </>
                                ) : (
                                    '+ SHORTLIST'
                                )}
                            </button>
                        </div>
                    )
                }
                {
                    applicationStatus === 'processing' &&
                    <div className="application-card-shortlist-bar" onClick={e => e.stopPropagation()}>
                        <span>Processing...</span>
                        <Spinner size="sm" className='me-2'> Loading... </Spinner>
                    </div>
                }
                {
                    applicationStatus === 'rejected' &&
                    <div className="application-card-shortlist-bar" onClick={e => e.stopPropagation()}>
                        <button className="theme-btn theme-btn  rejected-btn w-100 application-card-shortlist-btn">Rejected</button>
                    </div>
                }
                {
                    applicationStatus === 'accepted' &&
                    <div className="application-card-shortlist-bar" onClick={e => e.stopPropagation()}>
                        <button className="theme-btn w-100 approved-btn application-card-shortlist-btn">Approved</button>
                    </div>
                }


            </div>
        </React.Fragment >

    )
}

export default ApplicationCard